import { MOCK_STOCK_POSITIONS } from "../modules/inventory-management/mock/stockPositionMock";
import { MOCK_STOCK_JOURNAL } from "../modules/inventory-management/mock/stockJournalMock";
import { MOCK_INVENTORY_PRODUCTS } from "../modules/inventory-management/mock/inventoryProductsMock";
import { MOCK_VENDORS } from "../modules/inventory-management/mock/vendorsMock";
import { MOCK_CUSTOMERS } from "../modules/inventory-management/mock/customersMock";
import { MOCK_WAREHOUSES } from "../modules/inventory-management/mock/warehousesMock";
import { MOCK_ALLOCATION_CUSTOMERS } from "../modules/inventory-management/mock/allocationMock";
import { MOCK_ALLOCATION_PRODUCTS, MOCK_BATCHES } from "../modules/inventory-management/mock/batchRecommendationMock";
import { MOCK_ALLOCATION_DETAILS } from "../modules/inventory-management/mock/allocationDetailMock";
import { MOCK_TRANSFER_WAREHOUSES, MOCK_TRANSFER_PRODUCTS, MOCK_TRANSFER_BATCHES } from "../modules/inventory-management/mock/stockTransferMock";
import { MOCK_PRODUCT_DETAILS, MOCK_PRODUCT_WAREHOUSE_INVENTORY } from "../modules/inventory-management/mock/stockPositionDetailMock";
import { MOCK_PRODUCT_JOURNAL_ENTRIES } from "../modules/inventory-management/mock/productJournalMock";
import { MOCK_REORDER_ALERTS } from "../modules/inventory-management/mock/reorderAlertsMock";

// Cutoff date for Dead Stock validation (180 days before Aug 4, 2026, is Feb 5, 2026)
const DEAD_STOCK_CUTOFF = new Date("2026-02-05");

export const inventoryManagementService = {
  getStockPositions: (filters = {}) => {
    // Process items dynamically
    const processed = MOCK_STOCK_POSITIONS.map((item) => {
      const availableStock = Math.max(
        0,
        item.totalStock - item.reservedStock - item.maintenanceReserve - item.defectiveStock
      );
      const stockValue = availableStock * item.listPrice;
      const isDeadStock = new Date(item.lastOutwardMovementDate) < DEAD_STOCK_CUTOFF;

      return {
        ...item,
        availableStock,
        stockValue,
        isDeadStock,
      };
    });

    // Apply filters
    let result = [...processed];

    if (filters.productId && filters.productId !== "All") {
      result = result.filter((item) => item.productId === filters.productId);
    }

    if (filters.warehouse && filters.warehouse !== "All") {
      result = result.filter((item) => item.warehouse === filters.warehouse);
    }

    if (filters.category && filters.category !== "All") {
      result = result.filter((item) => item.category === filters.category);
    }

    if (filters.deadStock && filters.deadStock !== "All") {
      if (filters.deadStock === "Dead Stock") {
        result = result.filter((item) => item.isDeadStock === true);
      } else if (filters.deadStock === "Active Stock") {
        result = result.filter((item) => item.isDeadStock === false);
      }
    }

    return Promise.resolve(result);
  },

  getStockJournal: (filters = {}) => {
    let result = [...MOCK_STOCK_JOURNAL];

    if (filters.productId && filters.productId !== "All") {
      result = result.filter((item) => item.productId === filters.productId);
    }

    if (filters.warehouse && filters.warehouse !== "All") {
      result = result.filter((item) => item.warehouse === filters.warehouse);
    }

    if (filters.movementType && filters.movementType !== "All") {
      result = result.filter((item) => item.movementType === filters.movementType);
    }

    if (filters.startDate) {
      const start = new Date(filters.startDate);
      result = result.filter((item) => new Date(item.date) >= start);
    }

    if (filters.endDate) {
      const end = new Date(filters.endDate);
      // Extend to the end of the selected day
      end.setHours(23, 59, 59, 999);
      result = result.filter((item) => new Date(item.date) <= end);
    }

    // Sort chronologically by date descending by default
    result.sort((a, b) => new Date(b.date) - new Date(a.date));

    return Promise.resolve(result);
  },

  getInventoryProducts: () => {
    return Promise.resolve(MOCK_INVENTORY_PRODUCTS);
  },

  getWarehouses: () => {
    return Promise.resolve(MOCK_WAREHOUSES);
  },

  getVendors: () => {
    return Promise.resolve(MOCK_VENDORS);
  },

  getCustomers: () => {
    return Promise.resolve(MOCK_CUSTOMERS);
  },

  saveInventoryReceipt: (payload) => {
    console.log("Mock saveInventoryReceipt requested payload:", payload);
    return Promise.resolve({ success: true });
  },

  getAllocationProducts: () => {
    return Promise.resolve(MOCK_ALLOCATION_PRODUCTS);
  },

  getAllocationCustomers: () => {
    return Promise.resolve(MOCK_ALLOCATION_CUSTOMERS);
  },

  getBatchRecommendations: (productId) => {
    const filtered = MOCK_BATCHES.filter((b) => b.productId === productId);
    return Promise.resolve(filtered);
  },

  createAllocation: (payload) => {
    console.log("Mock createAllocation payload saved:", payload);
    return Promise.resolve({ success: true, allocationId: "1" });
  },

  getAllocationDetail: (id) => {
    const detail = MOCK_ALLOCATION_DETAILS[id] || MOCK_ALLOCATION_DETAILS["1"];
    return Promise.resolve(detail);
  },

  submitRevokeRequest: (payload) => {
    console.log("Mock submitRevokeRequest reason received:", payload);
    return Promise.resolve({ success: true });
  },

  getTransferWarehouses: () => {
    return Promise.resolve(MOCK_TRANSFER_WAREHOUSES);
  },

  getTransferProducts: (sourceWarehouseId) => {
    const matchingBatches = MOCK_TRANSFER_BATCHES.filter(
      (b) => b.warehouseId === sourceWarehouseId && b.availableQty > 0
    );
    const productIds = Array.from(new Set(matchingBatches.map((b) => b.productId)));
    const filteredProducts = MOCK_TRANSFER_PRODUCTS.filter((p) => productIds.includes(p.id));
    return Promise.resolve(filteredProducts);
  },

  getTransferBatches: (sourceWarehouseId, productId) => {
    const filtered = MOCK_TRANSFER_BATCHES.filter(
      (b) => b.warehouseId === sourceWarehouseId && b.productId === productId && b.availableQty > 0
    );
    return Promise.resolve(filtered);
  },

  createStockTransfer: (payload) => {
    console.log("=== SIMULATED STOCK TRANSFER PAYLOAD ===");
    console.log(JSON.stringify(payload, null, 2));
    
    console.log("=== SIMULATED INVENTORY ADJUSTMENT ===");
    console.log(`Source Warehouse ID: ${payload.sourceWarehouseId}`);
    console.log(`Destination Warehouse ID: ${payload.destinationWarehouseId}`);
    console.log(`Product ID: ${payload.productId}`);
    console.log(`Batch: ${payload.batchNumber}`);
    console.log(`Quantity Transferred: ${payload.transferQty}`);
    console.log(`Source Available Qty Decreased to: ${payload.availableQty - payload.transferQty}`);
    console.log(`Destination Available Qty Increased by: ${payload.transferQty}`);
    console.log("=========================================");
    return Promise.resolve({ success: true });
  },

  getStockPositionDetail: (productId) => {
    const product = MOCK_PRODUCT_DETAILS[productId] || MOCK_PRODUCT_DETAILS["prod-101"];
    const warehouses = MOCK_PRODUCT_WAREHOUSE_INVENTORY[productId] || MOCK_PRODUCT_WAREHOUSE_INVENTORY["prod-101"];
    return Promise.resolve({ product, warehouses });
  },

  getProductJournal: (productId, filters = {}) => {
    let result = MOCK_PRODUCT_JOURNAL_ENTRIES[productId] || MOCK_PRODUCT_JOURNAL_ENTRIES["prod-101"] || [];

    if (filters.warehouse && filters.warehouse !== "All") {
      result = result.filter((item) => item.warehouse === filters.warehouse);
    }

    if (filters.movementType && filters.movementType !== "All") {
      result = result.filter((item) => item.movementType === filters.movementType);
    }

    if (filters.startDate) {
      const start = new Date(filters.startDate);
      result = result.filter((item) => new Date(item.date) >= start);
    }

    if (filters.endDate) {
      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((item) => new Date(item.date) <= end);
    }

    result = [...result].sort((a, b) => new Date(b.date) - new Date(a.date));

    return Promise.resolve(result);
  },

  getReorderAlerts: (filters = {}) => {
    const today = new Date("2026-08-05");

    let processed = MOCK_REORDER_ALERTS.map((item) => {
      if (item.status === "Snoozed" && item.snoozeUntil) {
        const expiry = new Date(item.snoozeUntil);
        if (expiry < today) {
          return {
            ...item,
            status: "New",
            snoozeUntil: null,
          };
        }
      }
      return item;
    });

    let result = processed.filter((item) => item.availableStock <= item.reorderQuantity);

    if (filters.productId && filters.productId !== "All") {
      result = result.filter((item) => item.productId === filters.productId);
    }

    if (filters.warehouse && filters.warehouse !== "All") {
      result = result.filter((item) => item.warehouse === filters.warehouse);
    }

    if (filters.status && filters.status !== "All") {
      result = result.filter((item) => item.status === filters.status);
    }

    if (filters.category && filters.category !== "All") {
      result = result.filter((item) => item.category === filters.category);
    }

    result.sort((a, b) => {
      const shortageA = a.reorderQuantity - a.availableStock;
      const shortageB = b.reorderQuantity - b.availableStock;
      
      if (shortageB !== shortageA) {
        return shortageB - shortageA;
      }
      return a.availableStock - b.availableStock;
    });

    return Promise.resolve(result);
  },

  acknowledgeReorderAlert: (id) => {
    const idx = MOCK_REORDER_ALERTS.findIndex((item) => item.id === id);
    if (idx !== -1) {
      MOCK_REORDER_ALERTS[idx] = {
        ...MOCK_REORDER_ALERTS[idx],
        status: "Acknowledged",
        lastUpdated: new Date().toISOString(),
      };
      console.log("Mock Alert Acknowledged successfully:", MOCK_REORDER_ALERTS[idx]);
    }
    return Promise.resolve({ success: true });
  },

  snoozeReorderAlert: (payload) => {
    const { id, days, notes } = payload;
    const idx = MOCK_REORDER_ALERTS.findIndex((item) => item.id === id);
    if (idx !== -1) {
      const today = new Date("2026-08-05");
      const snoozeDate = new Date(today);
      snoozeDate.setDate(snoozeDate.getDate() + Number(days));

      MOCK_REORDER_ALERTS[idx] = {
        ...MOCK_REORDER_ALERTS[idx],
        status: "Snoozed",
        snoozeUntil: snoozeDate.toISOString(),
        lastUpdated: new Date().toISOString(),
        notes: notes || null,
      };
      console.log("Mock Alert Snoozed successfully payload:", MOCK_REORDER_ALERTS[idx]);
    }
    return Promise.resolve({ success: true });
  },
};
